/**
 * Iter 195 — Master Documents (GMP QMS Phase 2 Core).
 *
 * Controlled internal documents (Forms/Lists/Reports/Instructions/SOPs).
 * L01/KYB/00 = MASTER DOKÜMAN LİSTESİ (system seed, protected).
 * Parallel to the existing GMP Documents/SOP module which handles
 * external evidence (COAs, calibration certificates, etc.) — that module is untouched.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Badge, Input, Select, Textarea, Modal } from "@/components/UI";
import { Plus, FileText, GitBranch, MapPin, Link2, Archive, ClipboardCheck, History, ShieldAlert, X, Eye, Download, Printer, Upload } from "lucide-react";
import ControlledDocUploadModal, { RevisionUploadModal } from "@/components/ControlledDocUploadModal";
import DocCodeEditModal from "@/components/DocCodeEditModal";
import { previewPdf, downloadPdf, printPdf, resolvePdfEndpoint, filenameFor } from "@/lib/pdfActions";
import { useAuth } from "@/lib/auth";
import { todayTR } from "@/lib/date";

const today = () => todayTR();
const fmtDate = (iso) => (iso || "").slice(0, 10);
const fmtDT = (iso) => (iso || "").slice(0, 19).replace("T", " ");

const DOC_STATUS_LEVEL = { DRAFT: "inactive", IN_REVIEW: "attention", ACTIVE: "ok", OBSOLETE: "inactive", ARCHIVED: "inactive" };
const REV_STATUS_LEVEL = { DRAFT: "inactive", IN_REVIEW: "attention", CHECKED: "critical", APPROVED: "critical", ACTIVE: "ok", OBSOLETE: "inactive", REJECTED: "missing" };
const REVIEW_LEVEL = { ok: "ok", due_soon: "attention", overdue: "missing" };
const REVIEW_LABEL = { ok: "OK", due_soon: "Yakında", overdue: "Gecikti" };

const CLASSIFICATION_LABEL = {
  BLANK_TEMPLATE_REQUIRED: "Boş Şablon",
  BOTH_TEMPLATE_AND_OPERATIONAL: "Şablon + Operasyonel",
  LIVE_LIST_OR_REPORT: "Canlı Liste/Rapor",
  OPERATIONAL_PDF_ONLY: "Sadece Operasyonel",
  CONTROLLED_CONTENT_ONLY: "Kontrollü İçerik",
  NOT_APPLICABLE: "Uygulanamaz",
  USER_CONFIRMATION_REQUIRED: "Kullanıcı Onayı Bekliyor",
};
const CLASSIFICATION_LEVEL = {
  BLANK_TEMPLATE_REQUIRED: "attention",
  BOTH_TEMPLATE_AND_OPERATIONAL: "ok",
  LIVE_LIST_OR_REPORT: "ok",
  OPERATIONAL_PDF_ONLY: "ok",
  CONTROLLED_CONTENT_ONLY: "critical",
  NOT_APPLICABLE: "inactive",
  USER_CONFIRMATION_REQUIRED: "missing",
};

// Iter 197 — inventory generation status colors + label map.
const GEN_STATUS_LEVEL = { EXISTS: "ok", PARTIAL: "attention", MISSING: "missing", PLANNED: "inactive", NOT_APPLICABLE: "inactive", UNKNOWN: "inactive" };
const GEN_STATUS_LABEL = { EXISTS: "Mevcut", PARTIAL: "Kısmi", MISSING: "Eksik", PLANNED: "Planlı", NOT_APPLICABLE: "Uygulanamaz", UNKNOWN: "Bilinmiyor" };
const APPROVAL_PROFILE_LABEL = {
  NONE: "—", PREPARED_APPROVED: "Hazırla+Onay", PREPARED_CHECKED_APPROVED: "Haz+Kntrl+Onay",
  SYSTEM_GENERATED: "Sistem", RECORD_BASED: "Kayıt Bazlı",
};

// Iter 198 — blank template status.
const BLANK_STATUS_LEVEL = { READY: "ok", PARTIAL: "attention", NOT_CONFIGURED: "inactive", NOT_APPLICABLE: "inactive" };
const BLANK_STATUS_LABEL = { READY: "Hazır", PARTIAL: "Kısmi", NOT_CONFIGURED: "Tanımlanmadı", NOT_APPLICABLE: "Uygulanamaz" };

// Compact capability badges (§33): show only when true, tooltip explains meaning.
function CapBadge({ on, label, title, tone = "indigo" }) {
  if (!on) return null;
  const cls = tone === "amber"
    ? "bg-amber-100 text-amber-800 border-amber-300"
    : tone === "emerald"
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : tone === "sky"
    ? "bg-sky-100 text-sky-800 border-sky-300"
    : "bg-indigo-100 text-indigo-800 border-indigo-300";
  return (
    <span title={title || label}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cls}`}>
      {label}
    </span>
  );
}

export default function MasterDocuments() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ document_types: [], relationship_types: [] });
  const [f, setF] = useState({ document_type: "", status: "", review_status: "", q: "",
    generation_status: "", module: "", pdf_available: "", telegram_eligible: "",
    filtered_output: "", blank_template_required: "", approval_profile: "" });
  const [inventoryReport, setInventoryReport] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const [modal, setModal] = useState(null);       // create/edit
  const [detail, setDetail] = useState(null);
  const [revModal, setRevModal] = useState(null);
  const [wfModal, setWfModal] = useState(null);   // { rev, action }
  const [withdrawModal, setWithdrawModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [locModal, setLocModal] = useState(null);
  const [relModal, setRelModal] = useState(null);
  const [revHistory, setRevHistory] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);          // iter322
  const [revUploadDoc, setRevUploadDoc] = useState(null);        // iter322
  const [codeEditDoc, setCodeEditDoc] = useState(null);          // iter327
  const { user } = useAuth();                                     // iter327
  const canManageCode = user?.role === "admin" || user?.system_authority === true;

  const load = async () => {
    // Iter 197: Use inventory endpoint so rows carry generation_status,
    // capability flags, module, approval_profile. Manual (non-managed)
    // rows are still returned when no inventory filter is applied.
    const p = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      if (["document_type", "status", "review_status", "q"].includes(k)) return; // handled below
      p[k] = v;
    });
    if (f.q) p.q = f.q;
    if (f.document_type) p.document_type = f.document_type;
    // Load enriched inventory rows (superset of /master-documents)
    let inventoryRows = [];
    try {
      inventoryRows = (await api.get("/gmp/master-documents/inventory", p)) || [];
    } catch (e) {
      // Fallback: users without inventory permission → legacy list
      inventoryRows = (await api.get("/gmp/master-documents", p)) || [];
    }
    // Client-side status / review_status filter (server side already accepts document_type)
    let out = inventoryRows;
    if (f.status) out = out.filter(r => r.status === f.status);
    if (f.review_status) out = out.filter(r => r.review_status === f.review_status);
    setRows(out);
  };
  useEffect(() => { load(); }, [f.document_type, f.status, f.review_status,
    f.generation_status, f.module, f.pdf_available, f.telegram_eligible,
    f.filtered_output, f.blank_template_required, f.approval_profile]);
  useEffect(() => { api.get("/gmp/master-documents-meta").then(setMeta).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/gmp/master-documents/inventory-report").then(setInventoryReport).catch(() => {});
  }, []);

  // Iter 198 — blank template coverage.
  const [blankCoverage, setBlankCoverage] = useState(null);
  useEffect(() => {
    api.get("/gmp/blank-template-coverage").then(setBlankCoverage).catch(() => {});
  }, []);
  const blankStatusById = useMemo(() => {
    const m = {};
    (blankCoverage?.rows || []).forEach(r => { m[r.id] = r.blank_template_status; });
    return m;
  }, [blankCoverage]);

  // Phase 4 UI — classification-driven compact icon action group. Every
  // controlled PDF that has a valid endpoint gets Önizle / İndir / Yazdır
  // that share the SAME backend PDF source (via `resolvePdfEndpoint`).
  //
  // iter233 — L01/KYB/00 (MASTER DOKÜMAN LİSTESİ) is a system seed record
  // with `inventory_key=null` / `functional_classification=null`, so the
  // generic LIST-type fallback in `resolvePdfEndpoint` would route it to
  // the `/blank-template` endpoint which correctly returns 409 NOT_CONFIGURED
  // (no controlled template definition for the seed row). Its canonical PDF
  // is the live report at `/api/gmp/reports/L01/pdf` which reflects the
  // current master document list from real data. Route L01 to that endpoint
  // BEFORE the generic resolver so preview/download/print all use the same
  // working source-of-truth.
  const PdfActionGroup = ({ row }) => {
    const isL01 = row.base_code === "L01/KYB"
      || (row.current_full_code || "").startsWith("L01/KYB");
    const ep = isL01
      ? { kind: "live", path: "/api/gmp/reports/L01/pdf" }
      : resolvePdfEndpoint(row);
    if (!ep) return null;
    const fname = filenameFor(row, ep.kind === "blank" ? "Bos_Sablon" : "");
    return (
      <>
        <button
          type="button"
          onClick={() => previewPdf(ep.path, fname)}
          title="PDF Önizle"
          data-testid={`md-act-preview-${row.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white">
          <Eye size={14}/>
        </button>
        <button
          type="button"
          onClick={() => downloadPdf(ep.path, fname)}
          title="PDF İndir"
          data-testid={`md-act-download-${row.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-800 border">
          <Download size={14}/>
        </button>
        <button
          type="button"
          onClick={() => printPdf(ep.path, fname)}
          title="PDF Yazdır"
          data-testid={`md-act-print-${row.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-800 border">
          <Printer size={14}/>
        </button>
      </>
    );
  };

  const openBlankTemplate = async (r) => {
    const path = `/api/gmp/master-documents/${r.id}/blank-template`;
    await previewPdf(path, filenameFor(r, "Bos_Sablon"));
  };

  // Phase 3 — Preview a controlled operational PDF for list/report-type
  // managed documents (system-generated, filtered_output = true). Record-based
  // documents (formsper record) are NOT previewed here — user opens them from
  // their source module.
  const openControlledPdf = async (r) => {
    const url = r.source_backend_route;
    if (!url) {
      alert("Bu doküman için doğrudan PDF endpoint tanımlı değil. Kaynak modülünden açınız.");
      return;
    }
    // Skip record-based per-record generators (contain path parameters like {id}).
    if (url.includes("{")) {
      alert("Bu form kayıt bazlıdır. PDF, kaynak modüldeki ilgili kayıt üzerinden açılır.");
      return;
    }
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("erp_token");
      const resp = await fetch(`${backendUrl}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        alert(`PDF açılamadı (${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const fname = `${r.current_full_code || r.base_code}_${(r.title || "Belge").replace(/\s+/g, "_")}.pdf`;
      const { openPdfPreview } = await import("../lib/pdfPreview");
      openPdfPreview(blob, fname);
    } catch (e) {
      alert("PDF açılamadı: " + (e.message || e));
    }
  };

  // Navigate to the ERP source module for record-based documents.
  const goToSourceModule = (r) => {
    if (!r.source_route) {
      alert("Kaynak modül tanımlı değil.");
      return;
    }
    window.location.hash = r.source_route;
  };

  const openCreate = () => setModal({ mode: "create", doc: {
    document_type: "FORM", title: "", description: "", owner_department: "Kalite Yönetim Birimi",
    responsible_role: "quality", review_period_months: 24,
    revision_reason: "İlk yayın", change_summary: "",
  } });

  const save = async () => {
    if (!modal.doc.title?.trim()) return alert("Başlık zorunlu");
    try {
      if (modal.mode === "create") await api.post("/gmp/master-documents", modal.doc);
      else await api.patch(`/gmp/master-documents/${modal.doc.id}`, modal.doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openDetail = async (r) => {
    try { const d = await api.get(`/gmp/master-documents/${r.id}`); setDetail(d); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const showHistory = async (r) => {
    try { const h = await api.get(`/gmp/master-documents/${r.id}/revisions`); setRevHistory({ doc: r, revisions: h }); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const newRevision = (r) => setRevModal({ doc: r, revision_reason: "", change_summary: "", template_reference: "" });
  const saveRevision = async () => {
    if (!revModal.revision_reason?.trim()) return alert("Revizyon nedeni zorunlu");
    try {
      await api.post(`/gmp/master-documents/${revModal.doc.id}/revisions`, revModal);
      setRevModal(null); load();
      if (detail && detail.id === revModal.doc.id) openDetail(revModal.doc);
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const doWorkflow = async () => {
    const { rev, action, notes, override_reason, effective_date, reason } = wfModal;
    try {
      if (action === "reject") await api.post(`/gmp/document-revisions/${rev.id}/reject`, { reason });
      else await api.post(`/gmp/document-revisions/${rev.id}/${action}`, { notes, override_reason, effective_date });
      setWfModal(null); load();
      if (detail) openDetail(detail);
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const doWithdraw = async () => {
    if (!withdrawModal.reason?.trim()) return alert("Neden zorunlu");
    try { await api.post(`/gmp/master-documents/${withdrawModal.doc.id}/withdraw`, { reason: withdrawModal.reason }); setWithdrawModal(null); load(); setDetail(null); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const doReview = async () => {
    try { await api.post(`/gmp/master-documents/${reviewModal.doc.id}/review`, reviewModal); setReviewModal(null); load(); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const addLoc = async () => {
    try { await api.post(`/gmp/master-documents/${locModal.doc.id}/used-locations`, {
      module: locModal.module, page: locModal.page, route: locModal.route, action: locModal.action, active: true,
    }); setLocModal(null); if (detail) openDetail(detail); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const rmLoc = async (docId, locId) => {
    if (!window.confirm("Kullanıldığı yer silinsin mi?")) return;
    try { await api.del(`/gmp/master-documents/${docId}/used-locations/${locId}`); if (detail) openDetail(detail); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const addRel = async () => {
    try { await api.post(`/gmp/master-documents/${relModal.doc.id}/relationships`, {
      target_master_document_id: relModal.target_id, relationship_type: relModal.type, notes: relModal.notes,
    }); setRelModal(null); if (detail) openDetail(detail); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (r) => { if (window.confirm(`${r.base_code} silinsin mi?`)) { try { await api.del(`/gmp/master-documents/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };

  const revActions = (rev) => {
    const s = rev.status;
    const acts = [];
    if (s === "DRAFT") acts.push(["submit", "İncelemeye Gönder"]);
    if (s === "IN_REVIEW") acts.push(["check", "Kontrol Et"]);
    if (s === "CHECKED") acts.push(["approve", "Onayla"]);
    if (s === "APPROVED") acts.push(["activate", "Yürürlüğe Al"]);
    if (!["ACTIVE", "OBSOLETE", "REJECTED"].includes(s)) acts.push(["reject", "Reddet"]);
    return acts;
  };

  const filtered = useMemo(() => {
    const q = f.q?.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.base_code + " " + r.current_full_code + " " + r.title).toLowerCase().includes(q));
  }, [rows, f.q]);

  return (
    <div>
      <div className="bg-indigo-50/60 border border-indigo-200 rounded-lg p-3 mb-3 text-xs text-indigo-900">
        <b>ℹ Master Doküman Sistemi:</b> Kontrollü iç dokümanlar (Form/Liste/Rapor/Talimat/SOP). Kod formatı <b>Xxx/KYB/rr</b>.
        <b> L01/KYB/00 (MASTER DOKÜMAN LİSTESİ)</b> sistem tarafından korunur, silinemez.
        Her dokümanın kendi <b>Önizle · İndir · Yazdır</b> butonları aşağıdaki tabloda İşlemler kolonundadır.
        Harici kanıt dokümanları (COA, sertifika, dış SDS) mevcut <b>Doküman & SOP</b> sekmesinde yönetilmeye devam eder.
      </div>

      {/* Iter 197 §35 — ERP Doküman Envanteri özet paneli (managed baseline). */}
      {inventoryReport && (
        <div className="bg-white border rounded-lg mb-3" data-testid="md-inventory-summary">
          <div className="px-3 py-2 border-b flex items-center justify-between bg-slate-50">
            <div className="text-sm font-semibold text-slate-700">ERP Doküman Envanteri Özeti</div>
            <button className="text-xs text-indigo-600 hover:underline"
              onClick={() => setShowSummary(v => !v)}
              data-testid="md-inventory-summary-toggle">
              {showSummary ? "Gizle" : "Göster"}
            </button>
          </div>
          {showSummary && (
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                <div className="border rounded p-2 bg-slate-50">
                  <div className="text-slate-500 text-[10px]">Envanter Baseline</div>
                  <div className="text-lg font-bold text-slate-800 tabular-nums" data-testid="md-summary-managed">{inventoryReport.managed_by_inventory}</div>
                </div>
                <div className="border rounded p-2 bg-emerald-50">
                  <div className="text-emerald-700 text-[10px]">Mevcut (EXISTS)</div>
                  <div className="text-lg font-bold text-emerald-800 tabular-nums">{inventoryReport.by_status?.EXISTS || 0}</div>
                </div>
                <div className="border rounded p-2 bg-amber-50">
                  <div className="text-amber-700 text-[10px]">Kısmi (PARTIAL)</div>
                  <div className="text-lg font-bold text-amber-800 tabular-nums">{inventoryReport.by_status?.PARTIAL || 0}</div>
                </div>
                <div className="border rounded p-2 bg-rose-50">
                  <div className="text-rose-700 text-[10px]">Eksik (MISSING)</div>
                  <div className="text-lg font-bold text-rose-800 tabular-nums">{inventoryReport.by_status?.MISSING || 0}</div>
                </div>
                <div className="border rounded p-2 bg-slate-50">
                  <div className="text-slate-500 text-[10px]">Planlı (PLANNED)</div>
                  <div className="text-lg font-bold text-slate-700 tabular-nums">{inventoryReport.by_status?.PLANNED || 0}</div>
                </div>
                <div className="border rounded p-2 bg-white">
                  <div className="text-slate-500 text-[10px]">Manuel / Diğer</div>
                  <div className="text-lg font-bold text-slate-700 tabular-nums" title="Envanter dışı: manuel, test veya UNKNOWN kayıtlar (ayrı gösterilir)">{inventoryReport.manual}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-7 gap-2 text-[11px]">
                {[
                  ["pdf_available", "PDF"], ["preview_available", "Önizleme"],
                  ["print_available", "Yazdır"], ["download_available", "İndir"],
                  ["telegram_eligible", "Telegram"], ["filtered_output", "Filtreli"],
                  ["blank_template_required", "Boş Şablon Gerek"],
                ].map(([k, lbl]) => (
                  <div key={k} className="border rounded px-2 py-1 flex items-center justify-between bg-indigo-50/50">
                    <span className="text-indigo-700">{lbl}</span>
                    <span className="font-semibold text-indigo-900 tabular-nums">{inventoryReport.capability_counts?.[k] || 0}</span>
                  </div>
                ))}
              </div>
              {/* Iter 198 — blank template coverage sub-panel */}
              {blankCoverage && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] pt-2 border-t"
                     data-testid="md-blank-template-coverage">
                  {[
                    ["READY", "Hazır", "bg-emerald-50 text-emerald-800"],
                    ["PARTIAL", "Kısmi", "bg-amber-50 text-amber-800"],
                    ["NOT_CONFIGURED", "Tanımlanmadı", "bg-slate-50 text-slate-700"],
                    ["NOT_APPLICABLE", "Uygulanamaz", "bg-slate-50 text-slate-500"],
                  ].map(([k, lbl, cls]) => (
                    <div key={k} className={`border rounded px-2 py-1 flex items-center justify-between ${cls}`}>
                      <span>Boş Şablon · {lbl}</span>
                      <span className="font-semibold tabular-nums" data-testid={`md-blank-count-${k}`}>{blankCoverage.counts?.[k] || 0}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Kod / başlık / envanter anahtarı" data-testid="md-search"/></div>
        <Select label="Tür" value={f.document_type} onChange={e => setF({ ...f, document_type: e.target.value })} data-testid="md-filter-type">
          <option value="">Tümü</option>
          {meta.document_types.map(t => <option key={t.key} value={t.key}>{t.label} ({t.prefix})</option>)}
        </Select>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="md-filter-status">
          <option value="">Tümü</option>
          <option value="DRAFT">Taslak</option><option value="IN_REVIEW">İncelemede</option>
          <option value="ACTIVE">Yürürlükte</option><option value="OBSOLETE">Yürürlükten Kaldırıldı</option>
          <option value="ARCHIVED">Arşiv</option>
        </Select>
        <Select label="Gözden Geç." value={f.review_status} onChange={e => setF({ ...f, review_status: e.target.value })} data-testid="md-filter-review">
          <option value="">Tümü</option><option value="ok">OK</option>
          <option value="due_soon">Yakında</option><option value="overdue">Gecikti</option>
        </Select>
        <Select label="Üretim Durumu" value={f.generation_status} onChange={e => setF({ ...f, generation_status: e.target.value })} data-testid="md-filter-gen-status">
          <option value="">Tümü</option>
          <option value="EXISTS">Mevcut</option><option value="PARTIAL">Kısmi</option>
          <option value="MISSING">Eksik</option><option value="PLANNED">Planlı</option>
        </Select>
        <Select label="Modül" value={f.module} onChange={e => setF({ ...f, module: e.target.value })} data-testid="md-filter-module">
          <option value="">Tümü</option>
          {Object.keys(inventoryReport?.by_module || {}).sort().map(m =>
            <option key={m} value={m}>{m}</option>)}
        </Select>
        <Select label="PDF" value={f.pdf_available} onChange={e => setF({ ...f, pdf_available: e.target.value })} data-testid="md-filter-pdf">
          <option value="">Tümü</option><option value="true">Var</option><option value="false">Yok</option>
        </Select>
        <Select label="Telegram" value={f.telegram_eligible} onChange={e => setF({ ...f, telegram_eligible: e.target.value })} data-testid="md-filter-telegram">
          <option value="">Tümü</option><option value="true">Uygun</option><option value="false">Değil</option>
        </Select>
        <Select label="Filtreli Rapor" value={f.filtered_output} onChange={e => setF({ ...f, filtered_output: e.target.value })} data-testid="md-filter-filtered">
          <option value="">Tümü</option><option value="true">Evet</option><option value="false">Hayır</option>
        </Select>
        <Select label="Onay Profili" value={f.approval_profile} onChange={e => setF({ ...f, approval_profile: e.target.value })} data-testid="md-filter-approval">
          <option value="">Tümü</option>
          {Object.entries(APPROVAL_PROFILE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <div className="flex-1"/>
        {canManageCode && <Btn onClick={openCreate} data-testid="md-new"><Plus size={14}/> Yeni Doküman</Btn>}
        {canManageCode && <Btn onClick={() => setUploadOpen(true)} variant="secondary" data-testid="md-upload-new"><Upload size={14}/> Belge Ekle (Dosya)</Btn>}
      </div>

      <div className="bg-white border rounded-lg overflow-x-auto">
        {/* Iter 202 — Denetim Arama Kutusu (kod veya doküman adı) with live autocomplete. */}
        <div className="p-3 border-b bg-slate-50/60 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              value={f.q}
              onChange={e => setF({ ...f, q: e.target.value })}
              placeholder="Doküman ara: F30, L01, OOS Formu, Üretim Emri..."
              data-testid="md-quick-search"
            />
            {f.q && (
              <button
                onClick={() => setF({ ...f, q: "" })}
                data-testid="md-search-clear"
                className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-700">
                <X size={14}/>
              </button>
            )}
            {/* Autocomplete dropdown */}
            {f.q && f.q.length >= 1 && (() => {
              const term = f.q.toLowerCase();
              const matches = rows.filter(r =>
                (r.current_full_code || r.base_code || "").toLowerCase().includes(term) ||
                (r.title || "").toLowerCase().includes(term)
              ).slice(0, 8);
              if (matches.length === 0) return null;
              return (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-72 overflow-auto"
                     data-testid="md-search-suggest">
                  {matches.map(r => (
                    <button key={r.id}
                            onClick={() => { setF({ ...f, q: r.current_full_code || r.base_code }); }}
                            data-testid={`md-search-hit-${r.id}`}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 flex items-center gap-2 border-b last:border-b-0">
                      <span className="font-mono text-slate-700 min-w-[95px]">
                        {r.current_full_code || r.base_code}
                      </span>
                      <span className="text-slate-800 flex-1 truncate">{r.title}</span>
                      <Badge level={DOC_STATUS_LEVEL[r.status]}>{r.status}</Badge>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
          <span className="text-xs text-slate-500">{filtered.length} kayıt</span>
        </div>
        <table className="w-full text-sm min-w-[1200px]" data-testid="md-table">
          <thead className="bg-slate-100 text-slate-700 text-xs"><tr>
            <th className="text-left px-3 py-2">Kod</th><th className="text-left px-3 py-2">Başlık</th>
            <th className="text-left px-3 py-2">Tür</th><th className="text-left px-3 py-2">Modül</th>
            <th className="text-left px-3 py-2">Rev</th>
            <th className="text-left px-3 py-2">Yürürlük</th><th className="text-left px-3 py-2">Durum</th>
            <th className="text-left px-3 py-2">Üretim</th>
            <th className="text-left px-3 py-2">Yetenekler</th>
            <th className="text-left px-3 py-2">Gözden Geç.</th>
            <th className="text-left px-3 py-2 w-72">İşlem</th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`md-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.current_full_code || r.base_code + "/00"}
                  {r.protected && <div className="text-[9px] text-indigo-600">🔒 KORUMALI</div>}
                  {r.managed_by_inventory && <div className="text-[9px] text-slate-500" title={r.inventory_key}>Envanter</div>}
                </td>
                <td className="px-3 py-2 font-semibold">
                  <button onClick={() => openDetail(r)}
                           className="text-indigo-700 hover:text-indigo-900 hover:underline text-left"
                           data-testid={`md-title-click-${r.id}`}
                           title="Önizle / Detay">
                    {r.title}
                  </button>
                  {r.approval_profile && r.approval_profile !== "NONE" && (
                    <div className="text-[10px] text-slate-500 font-normal">{APPROVAL_PROFILE_LABEL[r.approval_profile] || r.approval_profile}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{meta.document_types.find(t => t.key === r.document_type)?.label || r.document_type}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.source_module || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">rev {String(r.current_revision_no).padStart(2, "0")}</td>
                <td className="px-3 py-2 text-xs">{r.status === "ACTIVE" ? fmtDate(r.updated_at || r.created_at) : "—"}</td>
                <td className="px-3 py-2"><Badge level={DOC_STATUS_LEVEL[r.status]}>{r.status}</Badge>
                  {r.functional_classification && (
                    <div className="mt-1">
                      <Badge level={CLASSIFICATION_LEVEL[r.functional_classification] || "inactive"}>
                        {CLASSIFICATION_LABEL[r.functional_classification] || r.functional_classification}
                      </Badge>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.generation_status ? (
                    <Badge level={GEN_STATUS_LEVEL[r.generation_status] || "inactive"}>
                      {GEN_STATUS_LABEL[r.generation_status] || r.generation_status}
                    </Badge>
                  ) : "—"}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1" data-testid={`md-caps-${r.id}`}>
                    <CapBadge on={r.pdf_available} label="PDF" title="PDF üretimi mevcut"/>
                    <CapBadge on={r.preview_available} label="Ön" title="Önizleme destekli" tone="sky"/>
                    <CapBadge on={r.print_available} label="Yz" title="Yazdırma destekli" tone="sky"/>
                    <CapBadge on={r.download_available} label="İn" title="İndirme destekli" tone="sky"/>
                    <CapBadge on={r.telegram_eligible} label="TG" title="Telegram ile gönderim uygun" tone="emerald"/>
                    <CapBadge on={r.filtered_output} label="Fil" title="Filtreli rapor çıktı" tone="indigo"/>
                    <CapBadge on={r.blank_template_required} label="Şb" title="Boş şablon gerekli (Iter198+)" tone="amber"/>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <Badge level={REVIEW_LEVEL[r.review_status] || "inactive"}>{REVIEW_LABEL[r.review_status] || "—"}</Badge>
                  {r.next_review_date && <div className="text-[10px] text-slate-500 mt-0.5">{r.next_review_date}</div>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1 items-center">
                    <Btn size="sm" variant="ghost" onClick={() => openDetail(r)} data-testid={`md-view-${r.id}`}>Detay</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => showHistory(r)} data-testid={`md-history-${r.id}`}><History size={11}/></Btn>
                    <PdfActionGroup row={r} />
                    {(r.functional_classification === "OPERATIONAL_PDF_ONLY" ||
                      r.functional_classification === "BOTH_TEMPLATE_AND_OPERATIONAL") && r.source_route && (
                      <Btn size="sm" variant="secondary" onClick={() => goToSourceModule(r)}
                           data-testid={`md-source-${r.id}`}
                           title="Operasyonel kayıt PDF'i için kaynak modüle gidin">
                        Kaynak Modüle Git
                      </Btn>
                    )}
                    {r.functional_classification === "USER_CONFIRMATION_REQUIRED" && (
                      <span className="text-[10px] px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-800 font-semibold"
                            data-testid={`md-userconfirm-${r.id}`}>
                        Kullanıcı Onayı Gerekli
                      </span>
                    )}
                    {r.status === "ACTIVE" && <Btn size="sm" variant="secondary" onClick={() => newRevision(r)} data-testid={`md-new-rev-${r.id}`}><GitBranch size={11}/> Yeni Rev.</Btn>}
                    {r.status === "ACTIVE" && <Btn size="sm" variant="ghost" onClick={() => setRevUploadDoc(r)} data-testid={`md-rev-upload-${r.id}`} title="Dosya ile yeni revizyon"><Upload size={11}/></Btn>}
                    {canManageCode && !r.protected && <Btn size="sm" variant="ghost" onClick={() => setCodeEditDoc(r)} data-testid={`md-code-edit-${r.id}`} title="Doküman kodunu düzenle"><FileText size={11}/></Btn>}
                    {!r.protected && r.status !== "ACTIVE" && <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`md-delete-${r.id}`}>Sil</Btn>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Master doküman kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* CREATE MODAL */}
      <Modal open={!!modal && modal.mode === "create"} onClose={() => setModal(null)} title="Yeni Master Doküman" size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <Select label="Tür *" value={modal.doc.document_type} onChange={e => setModal({ ...modal, doc: { ...modal.doc, document_type: e.target.value } })} data-testid="md-form-type">
            {meta.document_types.map(t => <option key={t.key} value={t.key}>{t.label} ({t.prefix})</option>)}
          </Select>
          <Input label="Sorumlu Rol" value={modal.doc.responsible_role || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, responsible_role: e.target.value } })}/>
          <div className="col-span-2"><Input label="Başlık *" value={modal.doc.title} onChange={e => setModal({ ...modal, doc: { ...modal.doc, title: e.target.value } })} data-testid="md-form-title"/></div>
          <div className="col-span-2"><Textarea label="Açıklama" rows={2} value={modal.doc.description || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, description: e.target.value } })}/></div>
          <Input label="Sorumlu Departman" value={modal.doc.owner_department || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, owner_department: e.target.value } })}/>
          <Input label="Gözden Geçirme (ay)" type="number" value={modal.doc.review_period_months || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, review_period_months: parseInt(e.target.value) || null } })} data-testid="md-form-review-period"/>
          <div className="col-span-2 border-t pt-2 mt-1 text-xs font-semibold text-slate-500 uppercase">İlk Revizyon (00)</div>
          <Input label="Hazırlayan (ad)" value={modal.doc.prepared_by_name || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, prepared_by_name: e.target.value } })}/>
          <Input label="Revizyon Nedeni" value={modal.doc.revision_reason} onChange={e => setModal({ ...modal, doc: { ...modal.doc, revision_reason: e.target.value } })}/>
          <div className="col-span-2"><Textarea label="Değişiklik Özeti" rows={2} value={modal.doc.change_summary || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, change_summary: e.target.value } })}/></div>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="md-form-save">Kaydet</Btn></div>
      </Modal>

      {/* DETAIL MODAL */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.current_full_code} — ${detail.title}` : ""} size="lg">
        {detail && (<div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><b>Kod:</b> <span className="font-mono">{detail.current_full_code}</span></div>
            <div><b>Tür:</b> {meta.document_types.find(t => t.key === detail.document_type)?.label}</div>
            <div><b>Durum:</b> <Badge level={DOC_STATUS_LEVEL[detail.status]}>{detail.status}</Badge></div>
            <div><b>Sorumlu:</b> {detail.owner_department || "—"}</div>
            <div><b>Rol:</b> {detail.responsible_role || "—"}</div>
            <div><b>Gözden Geç.:</b> {detail.review_period_months ? `${detail.review_period_months} ay` : "—"}</div>
            <div><b>Son Gözden Geç.:</b> {detail.last_review_date || "—"}</div>
            <div><b>Sonraki:</b> {detail.next_review_date || "—"} <Badge level={REVIEW_LEVEL[detail.review_status] || "inactive"}>{REVIEW_LABEL[detail.review_status] || "—"}</Badge></div>
          </div>

          <div className="border-t pt-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase font-semibold text-slate-600">Güncel Revizyon</div>
              {detail.current_revision && detail.current_revision.status !== "ACTIVE" && (
                <div className="flex gap-1">
                  {revActions(detail.current_revision).map(([action, label]) => (
                    <Btn key={action} size="sm" variant={action === "reject" ? "danger" : "primary"} onClick={() => setWfModal({ rev: detail.current_revision, action })} data-testid={`md-wf-${action}`}>{label}</Btn>
                  ))}
                </div>
              )}
            </div>
            {detail.current_revision && (
              <div className="bg-white border rounded p-2 text-xs grid grid-cols-4 gap-2">
                <div><b>Rev:</b> <span className="font-mono">{detail.current_revision.full_code}</span></div>
                <div><b>Durum:</b> <Badge level={REV_STATUS_LEVEL[detail.current_revision.status]}>{detail.current_revision.status}</Badge></div>
                <div><b>Etkinlik:</b> {detail.current_revision.effective_date || "—"}</div>
                <div><b>Onay:</b> {fmtDate(detail.current_revision.approved_at)}</div>
                <div className="col-span-2"><b>Hazırlayan:</b> {detail.current_revision.prepared_by_name}</div>
                <div className="col-span-2"><b>Kontrol:</b> {detail.current_revision.checked_by_name || "—"}</div>
                <div className="col-span-2"><b>Onaylayan:</b> {detail.current_revision.approved_by_name || "—"}</div>
                <div className="col-span-2"><b>Neden:</b> {detail.current_revision.revision_reason || "—"}</div>
                {detail.current_revision.sod_override && <div className="col-span-4 bg-amber-50 border border-amber-200 rounded p-1"><b>⚠ SoD Override:</b> {detail.current_revision.sod_override_reason}</div>}
              </div>
            )}
          </div>

          {/* Used Locations */}
          <div className="border-t pt-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase font-semibold text-slate-600 flex items-center gap-1"><MapPin size={12}/> Kullanıldığı Yerler ({(detail.used_locations || []).length})</div>
              <Btn size="sm" onClick={() => setLocModal({ doc: detail, module: "", page: "", route: "", action: "" })} data-testid="md-loc-add"><Plus size={11}/> Ekle</Btn>
            </div>
            <div className="bg-white border rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Modül</th><th className="text-left px-2 py-1">Sayfa</th><th className="text-left px-2 py-1">Route</th><th className="text-left px-2 py-1">Aktif</th><th></th></tr></thead>
                <tbody>
                  {(detail.used_locations || []).map(l => (
                    <tr key={l.id} className="border-t"><td className="px-2 py-1">{l.module}</td><td className="px-2 py-1">{l.page || "—"}</td><td className="px-2 py-1 font-mono">{l.route || "—"}</td><td className="px-2 py-1">{l.active ? "✓" : "—"}</td><td className="px-2 py-1">{l.id && <button onClick={() => rmLoc(detail.id, l.id)} className="text-red-600 text-xs">Sil</button>}</td></tr>
                  ))}
                  {(detail.used_locations || []).length === 0 && <tr><td colSpan={5} className="text-center py-2 text-slate-400">—</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-t pt-2 flex flex-wrap gap-2">
            <Btn size="sm" variant="secondary" onClick={() => showHistory(detail)}><History size={12}/> Revizyon Geçmişi</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setRelModal({ doc: detail, target_id: "", type: "RELATED_TO", notes: "" })}><Link2 size={12}/> İlişki Ekle</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setReviewModal({ doc: detail, review_result: "unchanged", review_notes: "" })} data-testid="md-review"><ClipboardCheck size={12}/> Gözden Geçir</Btn>
            {detail.status === "ACTIVE" && <Btn size="sm" variant="secondary" onClick={() => newRevision(detail)}><GitBranch size={12}/> Yeni Revizyon</Btn>}
            {!detail.protected && detail.status !== "OBSOLETE" && <Btn size="sm" variant="danger" onClick={() => setWithdrawModal({ doc: detail, reason: "" })} data-testid="md-withdraw"><Archive size={12}/> Yürürlükten Kaldır</Btn>}
          </div>
        </div>)}
      </Modal>

      {/* NEW REVISION */}
      <Modal open={!!revModal} onClose={() => setRevModal(null)} title={revModal ? `Yeni Revizyon: ${revModal.doc.base_code}` : ""} size="md">
        {revModal && (<div className="space-y-3">
          <Input label="Revizyon Nedeni *" value={revModal.revision_reason} onChange={e => setRevModal({ ...revModal, revision_reason: e.target.value })} data-testid="md-rev-reason"/>
          <Textarea label="Değişiklik Özeti" rows={3} value={revModal.change_summary} onChange={e => setRevModal({ ...revModal, change_summary: e.target.value })} data-testid="md-rev-summary"/>
          <Input label="Template Referansı (opsiyonel)" value={revModal.template_reference} onChange={e => setRevModal({ ...revModal, template_reference: e.target.value })}/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setRevModal(null)}>Vazgeç</Btn><Btn onClick={saveRevision} data-testid="md-rev-save">Kaydet</Btn></div>
      </Modal>

      {/* WORKFLOW MODAL */}
      <Modal open={!!wfModal} onClose={() => setWfModal(null)} title={wfModal ? `İşlem: ${wfModal.action.toUpperCase()}` : ""} size="sm">
        {wfModal && (<div className="space-y-3">
          {wfModal.action === "reject" ? <Textarea label="Ret Gerekçesi *" rows={3} value={wfModal.reason || ""} onChange={e => setWfModal({ ...wfModal, reason: e.target.value })} data-testid="md-wf-reject-reason"/>
            : <Textarea label="Notlar" rows={2} value={wfModal.notes || ""} onChange={e => setWfModal({ ...wfModal, notes: e.target.value })} data-testid="md-wf-notes"/>}
          {wfModal.action === "approve" && <>
            <Input label="Etkinlik Tarihi" type="date" value={wfModal.effective_date || today()} onChange={e => setWfModal({ ...wfModal, effective_date: e.target.value })} data-testid="md-wf-effective"/>
            <Textarea label="SoD Override Gerekçesi (aynı kullanıcı hazırladıysa)" rows={2} value={wfModal.override_reason || ""} onChange={e => setWfModal({ ...wfModal, override_reason: e.target.value })} data-testid="md-wf-override"/>
          </>}
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setWfModal(null)}>Vazgeç</Btn><Btn onClick={doWorkflow} data-testid="md-wf-confirm">Uygula</Btn></div>
      </Modal>

      {/* WITHDRAW */}
      <Modal open={!!withdrawModal} onClose={() => setWithdrawModal(null)} title="Yürürlükten Kaldır" size="sm">
        {withdrawModal && <Textarea label="Neden *" rows={3} value={withdrawModal.reason} onChange={e => setWithdrawModal({ ...withdrawModal, reason: e.target.value })} data-testid="md-withdraw-reason"/>}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setWithdrawModal(null)}>Vazgeç</Btn><Btn variant="danger" onClick={doWithdraw} data-testid="md-withdraw-confirm">Yürürlükten Kaldır</Btn></div>
      </Modal>

      {/* REVIEW */}
      <Modal open={!!reviewModal} onClose={() => setReviewModal(null)} title="Periyodik Gözden Geçirme" size="sm">
        {reviewModal && (<div className="space-y-3">
          <Select label="Sonuç" value={reviewModal.review_result} onChange={e => setReviewModal({ ...reviewModal, review_result: e.target.value })} data-testid="md-review-result">
            <option value="unchanged">Değişiklik gerekmiyor</option>
            <option value="revision_required">Revizyon gerekli</option>
            <option value="archived">Arşivle</option>
          </Select>
          <Textarea label="Notlar" rows={3} value={reviewModal.review_notes} onChange={e => setReviewModal({ ...reviewModal, review_notes: e.target.value })}/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setReviewModal(null)}>Vazgeç</Btn><Btn onClick={doReview} data-testid="md-review-save">Kaydet</Btn></div>
      </Modal>

      {/* USED LOCATION */}
      <Modal open={!!locModal} onClose={() => setLocModal(null)} title="Kullanıldığı Yer Ekle" size="sm">
        {locModal && (<div className="space-y-3">
          <Input label="Modül *" value={locModal.module} onChange={e => setLocModal({ ...locModal, module: e.target.value })} placeholder="production / gmp / inventory..." data-testid="md-loc-module"/>
          <Input label="Sayfa" value={locModal.page} onChange={e => setLocModal({ ...locModal, page: e.target.value })}/>
          <Input label="Route" value={locModal.route} onChange={e => setLocModal({ ...locModal, route: e.target.value })} placeholder="/production"/>
          <Input label="Aksiyon" value={locModal.action} onChange={e => setLocModal({ ...locModal, action: e.target.value })}/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setLocModal(null)}>Vazgeç</Btn><Btn onClick={addLoc} data-testid="md-loc-save">Ekle</Btn></div>
      </Modal>

      {/* RELATIONSHIP */}
      <Modal open={!!relModal} onClose={() => setRelModal(null)} title="İlişki Ekle" size="sm">
        {relModal && (<div className="space-y-3">
          <Select label="Hedef Doküman" value={relModal.target_id} onChange={e => setRelModal({ ...relModal, target_id: e.target.value })} data-testid="md-rel-target">
            <option value="">— Seç —</option>
            {rows.filter(r => r.id !== relModal.doc.id).map(r => <option key={r.id} value={r.id}>{r.current_full_code} — {r.title}</option>)}
          </Select>
          <Select label="İlişki Tipi" value={relModal.type} onChange={e => setRelModal({ ...relModal, type: e.target.value })}>
            {(meta.relationship_types || []).map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Textarea label="Notlar" rows={2} value={relModal.notes} onChange={e => setRelModal({ ...relModal, notes: e.target.value })}/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setRelModal(null)}>Vazgeç</Btn><Btn onClick={addRel} data-testid="md-rel-save">Ekle</Btn></div>
      </Modal>

      {/* REVISION HISTORY */}
      <Modal open={!!revHistory} onClose={() => setRevHistory(null)} title={revHistory ? `Revizyon Geçmişi: ${revHistory.doc.base_code}` : ""} size="lg">
        {revHistory && (
          <div className="bg-white border rounded overflow-x-auto">
            <table className="w-full text-xs" data-testid="md-history-table">
              <thead className="bg-slate-100"><tr>
                <th className="text-left px-2 py-1">Rev</th><th className="text-left px-2 py-1">Durum</th>
                <th className="text-left px-2 py-1">Neden</th><th className="text-left px-2 py-1">Hazırlayan</th>
                <th className="text-left px-2 py-1">Onaylayan</th><th className="text-left px-2 py-1">Etkinlik</th>
                <th className="text-left px-2 py-1">Obs. Tarihi</th>
              </tr></thead>
              <tbody>
                {revHistory.revisions.map(r => (
                  <tr key={r.id} className="border-t" data-testid={`md-rev-history-${r.id}`}>
                    <td className="px-2 py-1 font-mono">{r.full_code}</td>
                    <td className="px-2 py-1"><Badge level={REV_STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                    <td className="px-2 py-1">{r.revision_reason || "—"}<div className="text-slate-500 text-[10px]">{(r.change_summary || "").slice(0, 100)}</div></td>
                    <td className="px-2 py-1">{r.prepared_by_name || "—"}</td>
                    <td className="px-2 py-1">{r.approved_by_name || "—"}</td>
                    <td className="px-2 py-1">{r.effective_date || "—"}</td>
                    <td className="px-2 py-1">{r.obsoleted_at ? fmtDate(r.obsoleted_at) : "—"}</td>
                  </tr>
                ))}
                {revHistory.revisions.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-slate-400">—</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* iter322 — File upload (create or revision) */}
      <ControlledDocUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={load}/>
      <RevisionUploadModal open={!!revUploadDoc} onClose={() => setRevUploadDoc(null)} doc={revUploadDoc} onCreated={load}/>
      <DocCodeEditModal open={!!codeEditDoc} onClose={() => setCodeEditDoc(null)} doc={codeEditDoc} onSaved={load}/>
    </div>
  );
}
